import { tourCopy } from '../copy';
import { TOUR } from '../targets';
import type { TourDefinition, TourStep } from '../tourTypes';

const c = tourCopy.offer;

export const OFFER_TOUR_IDS = {
  gigCreator: 'offer-gig-creator',
  gigRecipient: 'offer-gig-recipient',
  standaloneCreator: 'offer-standalone-creator',
  standaloneRecipient: 'offer-standalone-recipient',
} as const;

export type OfferTourRole = keyof typeof OFFER_TOUR_IDS;

/** The offer tour for the viewer's side. On gig offers the recipient requests payment. */
export const offerTourId = (gigOffer: boolean, isCreator: boolean): string =>
  OFFER_TOUR_IDS[
    gigOffer
      ? isCreator
        ? 'gigCreator'
        : 'gigRecipient'
      : isCreator
        ? 'standaloneCreator'
        : 'standaloneRecipient'
  ];

export const offerFormTour: TourDefinition = {
  id: 'offer-form',
  version: 1,
  title: tourCopy.offerForm.title,
  description: tourCopy.offerForm.description,
  route: '/offers/create',
  steps: [
    {
      ...tourCopy.offerForm.recipient,
      target: TOUR.offerFormRecipient,
      placement: 'bottom',
      optional: true,
    },
    { ...tourCopy.offerForm.price, target: TOUR.offerFormPrice, placement: 'bottom' },
    {
      ...tourCopy.offerForm.schedule,
      target: TOUR.offerFormSchedule,
      placement: 'top',
      optional: true,
    },
    {
      ...tourCopy.offerForm.expiration,
      target: TOUR.offerFormExpiration,
      placement: 'top',
      optional: true,
    },
    { ...tourCopy.offerForm.fees, target: TOUR.offerFormFees, placement: 'left', optional: true },
    { ...tourCopy.offerForm.chat },
  ],
};

/**
 * Steps shared by every side. Most are optional because what's on the page
 * depends on the offer's state (e.g. no Payments list before the first payment).
 */
const sharedTail: TourStep[] = [
  { ...c.settlement, target: TOUR.offerSettlement, placement: 'top', optional: true },
  { ...c.offPlatform, target: TOUR.offerOffPlatform, placement: 'top', optional: true },
  { ...c.paymentsList, target: TOUR.offerPaymentsList, placement: 'top', optional: true },
];

const head = (recipient: boolean): TourStep[] => [
  { ...c.summary, target: TOUR.offerSummary, placement: 'left', optional: true },
  ...(recipient
    ? [{ ...c.respond, target: TOUR.offerResponse, placement: 'left' as const, optional: true }]
    : []),
  { ...c.schedule, target: TOUR.offerSchedule, placement: 'top', optional: true },
];

const offerTour = (
  role: OfferTourRole,
  copy: { title: string; description: string; action: { title: string; body: string } },
  recipient: boolean,
): TourDefinition => ({
  id: OFFER_TOUR_IDS[role],
  version: 1,
  title: copy.title,
  description: copy.description,
  route: '/offers/$offerId',
  // Started by the offer page, which knows which side the viewer is on.
  autoStart: false,
  steps: [
    ...head(recipient),
    { ...copy.action, target: TOUR.offerPaymentAction, placement: 'top', optional: true },
    ...sharedTail,
  ],
});

export const offerTours: TourDefinition[] = [
  offerTour('gigCreator', tourCopy.offerGigCreator, false),
  offerTour('gigRecipient', tourCopy.offerGigRecipient, true),
  offerTour('standaloneCreator', tourCopy.offerStandaloneCreator, false),
  offerTour('standaloneRecipient', tourCopy.offerStandaloneRecipient, true),
];
